/**
 * Dialog Component
 *
 * A modal dialog component for displaying content in an overlay.
 * Supports accessibility features and keyboard navigation.
 */

'use client';

import React from 'react';
import { cn } from '@/lib/utils';
import { X } from 'lucide-react';

// =============================================================================
// TYPES
// =============================================================================

export interface DialogProps {
  /**
   * Whether the dialog is open
   */
  open?: boolean;

  /**
   * Callback when the dialog should close
   */
  onOpenChange?: (open: boolean) => void;

  /**
   * Children components (DialogContent, etc.)
   */
  children?: React.ReactNode;
}

export interface DialogContentProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Whether to show the close button
   */
  showClose?: boolean;

  /**
   * Size of the dialog
   */
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | 'full';
}

export interface DialogHeaderProps
  extends React.HTMLAttributes<HTMLDivElement> {}

export interface DialogFooterProps
  extends React.HTMLAttributes<HTMLDivElement> {}

export interface DialogTitleProps
  extends React.HTMLAttributes<HTMLHeadingElement> {}

export interface DialogDescriptionProps
  extends React.HTMLAttributes<HTMLParagraphElement> {}

// =============================================================================
// CONTEXT
// =============================================================================

interface DialogContextValue {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DialogContext = React.createContext<DialogContextValue>({
  open: false,
  onOpenChange: () => {},
});

// =============================================================================
// STYLES
// =============================================================================

const getSizeClasses = (size: DialogContentProps['size']) => {
  switch (size) {
    case 'sm':
      return 'max-w-sm';
    case 'md':
      return 'max-w-md';
    case 'lg':
      return 'max-w-lg';
    case 'xl':
      return 'max-w-xl';
    case '2xl':
      return 'max-w-2xl';
    case '3xl':
      return 'max-w-3xl';
    case 'full':
      return 'max-w-full mx-4';
    default:
      return 'max-w-lg';
  }
};

// =============================================================================
// COMPONENTS
// =============================================================================

/**
 * Main Dialog component
 */
export const Dialog: React.FC<DialogProps> = ({
  open = false,
  onOpenChange = () => {},
  children,
}) => {
  return (
    <DialogContext.Provider value={{ open, onOpenChange }}>
      {children}
    </DialogContext.Provider>
  );
};

Dialog.displayName = 'Dialog';

/**
 * Dialog Content component
 */
export const DialogContent = React.forwardRef<
  HTMLDivElement,
  DialogContentProps
>(({ className, showClose = true, size = 'lg', children, ...props }, ref) => {
  const { open, onOpenChange } = React.useContext(DialogContext);

  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        onOpenChange(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onOpenChange]);

  // Prevent body scroll when dialog is open
  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [open]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={() => onOpenChange(false)}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          ref={ref}
          role="dialog"
          aria-modal="true"
          className={cn(
            'relative w-full rounded-lg bg-[var(--surface-card)] shadow-lg',
            'animate-in fade-in-0 zoom-in-95',
            getSizeClasses(size),
            className
          )}
          onClick={(e) => e.stopPropagation()}
          {...props}
        >
          {showClose && (
            <button
              onClick={() => onOpenChange(false)}
              className={cn(
                'absolute right-4 top-4 rounded-sm opacity-70',
                'ring-offset-[var(--surface-card)] transition-opacity',
                'hover:opacity-100 focus:outline-none focus:ring-2',
                'focus:ring-[var(--border-default)] focus:ring-offset-2',
                'disabled:pointer-events-none'
              )}
              aria-label="Close dialog"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </button>
          )}
          {children}
        </div>
      </div>
    </>
  );
});

DialogContent.displayName = 'DialogContent';

/**
 * Dialog Header component
 */
export const DialogHeader = React.forwardRef<HTMLDivElement, DialogHeaderProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-col space-y-1.5 px-6 py-4 border-b border-[var(--border-default)]',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

DialogHeader.displayName = 'DialogHeader';

/**
 * Dialog Footer component
 */
export const DialogFooter = React.forwardRef<HTMLDivElement, DialogFooterProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center justify-end gap-3 px-6 py-4 border-t border-[var(--border-default)]',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

DialogFooter.displayName = 'DialogFooter';

/**
 * Dialog Title component
 */
export const DialogTitle = React.forwardRef<
  HTMLHeadingElement,
  DialogTitleProps
>(({ className, children, ...props }, ref) => {
  return (
    <h2
      ref={ref}
      className={cn('text-lg font-semibold text-[var(--text-heading)]', className)}
      {...props}
    >
      {children}
    </h2>
  );
});

DialogTitle.displayName = 'DialogTitle';

/**
 * Dialog Description component
 */
export const DialogDescription = React.forwardRef<
  HTMLParagraphElement,
  DialogDescriptionProps
>(({ className, children, ...props }, ref) => {
  return (
    <p
      ref={ref}
      className={cn('text-sm text-[var(--text-secondary)]', className)}
      {...props}
    >
      {children}
    </p>
  );
});

DialogDescription.displayName = 'DialogDescription';

// =============================================================================
// EXPORTS
// =============================================================================

export default Dialog;
